import React, { useEffect, useState } from 'react';
import { Box, ButtonBase, Collapse, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import LessonRow from './LessonRow';
import { countCompleted } from '../useTutorialProgress';

/**
 * Menu lateral da página de tutoriais: capítulos em sanfona, cada um com suas lições.
 * O capítulo da lição aberta fica expandido; a pessoa abre e fecha os outros livremente.
 * @param {Array} chapters - [{ id, number, title, description, lessons: [...] }]
 * @param {String} selectedChapterId - capítulo da lição aberta
 * @param {Number} currentIndex - lição aberta dentro do capítulo
 * @param {Function} onSelect - recebe (chapterId, índice da lição)
 * @param {Object} progressById - progresso por id da lição (opcional)
 */
const ChapterAccordion = ({ chapters, selectedChapterId, currentIndex, onSelect, progressById = null }) => {
  const [expanded, setExpanded] = useState(() => new Set(selectedChapterId ? [selectedChapterId] : []));

  // Ao trocar de capítulo (contagem do fim do vídeo, setas), o capítulo novo se abre
  useEffect(() => {
    if (!selectedChapterId) return;
    setExpanded((current) => (current.has(selectedChapterId) ? current : new Set(current).add(selectedChapterId)));
  }, [selectedChapterId]);

  const toggle = (chapterId) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(chapterId)) next.delete(chapterId);
    else next.add(chapterId);
    return next;
  });

  return (
    <Box component="nav" aria-label="Capítulos dos tutoriais" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {chapters.map((chapter) => {
        const lessons = chapter.lessons || [];
        const open = expanded.has(chapter.id);
        const current = chapter.id === selectedChapterId;
        const done = progressById ? countCompleted(lessons, progressById) : 0;
        const allDone = lessons.length > 0 && done === lessons.length;
        return (
          <Box key={chapter.id}>
            <ButtonBase
              onClick={() => toggle(chapter.id)}
              aria-expanded={open}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                textAlign: 'left',
                gap: 1,
                px: 1,
                py: 0.9,
                borderRadius: '12px',
                '&:hover': { backgroundColor: ColorPalette.bgMain }
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: '7px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  backgroundColor: allDone ? ColorPalette.successLight : ColorPalette.blueAlpha,
                  color: allDone ? '#2E7D32' : ColorPalette.blueMain
                }}
              >
                {allDone ? <CheckIcon sx={{ fontSize: 14 }} /> : chapter.number}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: '#1A1A2E',
                    lineHeight: 1.3
                  }}
                >
                  {chapter.title}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: ColorPalette.textLight, mt: 0.1 }}>
                  {progressById
                    ? `${done} de ${lessons.length} assistidas`
                    : `${lessons.length} ${lessons.length === 1 ? 'lição' : 'lições'}`}
                </Typography>
              </Box>
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: ColorPalette.textLight,
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease'
                }}
              />
            </ButtonBase>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                  ml: '20px',
                  mr: 0.5,
                  mb: 1,
                  pl: 0.75,
                  borderLeft: `2px solid ${ColorPalette.border}`
                }}
              >
                {lessons.map((lesson, index) => (
                  <LessonRow
                    key={lesson.id}
                    lesson={lesson}
                    index={index}
                    selected={current && index === currentIndex}
                    progress={progressById ? progressById[lesson.id] : null}
                    onClick={() => onSelect(chapter.id, index)}
                    compact
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default ChapterAccordion;
